import { useCallback, useEffect, useRef, useState } from "react";
import { X, Eye, Trash2, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc, formatErr } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";
import { timeAgo } from "@/lib/timeAgo";
import { StoryReplyBar } from "@/components/stories/StoryReplyBar";

const IMAGE_MS = 5000;

function Viewers({ id, onClose }) {
  const [list, setList] = useState(null);
  useEffect(() => { api.get(`/stories/${id}/viewers`).then(({ data }) => setList(data)).catch(() => setList([])); }, [id]);
  return (
    <div className="absolute inset-x-0 bottom-0 z-30 max-h-[55%] overflow-y-auto bg-white rounded-t-3xl p-4 space-y-2" data-testid="story-viewers" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between"><p className="font-bold text-sm">Angesehen von {list?.length ?? "…"}</p><button onClick={onClose} className="p-1 text-slate-400"><X className="w-4 h-4" /></button></div>
      {list?.map((u) => <div key={u.user_id} className="flex items-center gap-2"><UserAvatar noStory userId={u.user_id} name={u.name} picture={u.picture} size="w-8 h-8" text="text-xs" /><span className="text-sm font-semibold flex-1">{u.name}</span>{u.reaction && <span className="text-lg" data-testid={`viewer-reaction-${u.user_id}`}>{u.reaction}</span>}</div>)}
      {list?.length === 0 && <p className="text-sm text-slate-400">Noch niemand</p>}
    </div>
  );
}

function useTimer(story, paused, next) {
  const [p, setP] = useState(0);
  useEffect(() => { setP(0); }, [story?.id]);
  useEffect(() => {
    if (!story || story.kind === "video" || paused) return;
    const t = setInterval(() => setP((x) => { const n = x + 100 / (IMAGE_MS / 50); if (n >= 100) { clearInterval(t); setTimeout(next, 0); } return Math.min(100, n); }), 50);
    return () => clearInterval(t);
  }, [story, paused, next]);
  return [p, setP];
}

export function StoryViewer({ groups, start, onClose, onSeen, onDeleted, onReacted = () => {} }) {
  const [gi, setGi] = useState(start);
  const [si, setSi] = useState(() => Math.max(0, groups[start].stories.findIndex((s) => !s.seen)));
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [viewers, setViewers] = useState(false);
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const vref = useRef(null);
  const g = groups[gi], story = g?.stories[si];
  const next = useCallback(() => {
    if (si < g.stories.length - 1) setSi(si + 1);
    else if (gi < groups.length - 1) { setGi(gi + 1); setSi(0); } else onClose();
  }, [si, gi, g, groups.length, onClose]);
  const prev = () => { if (si > 0) setSi(si - 1); else if (gi > 0) { setGi(gi - 1); setSi(0); } };
  const [prog, setProg] = useTimer(story, paused || viewers || typing, next);
  useEffect(() => { const v = vref.current; if (!v) return; typing ? v.pause() : v.play().catch(() => {}); }, [typing]);
  useEffect(() => {
    if (!story || story.seen) return;
    api.post(`/stories/${story.id}/view`).then(({ data }) => { if (data.points_earned) window.dispatchEvent(new Event("points-changed")); }).catch(() => {});
    onSeen(story.id);
  }, [story, onSeen]);
  if (!story) return null;
  const del = async () => {
    if (!window.confirm("Story löschen?")) return;
    try { await api.delete(`/stories/${story.id}`); toast.success("Story gelöscht"); onDeleted(); onClose(); } catch (e) { toast.error(formatErr(e)); }
  };
  const tap = (e) => { const r = e.currentTarget.getBoundingClientRect(); if (e.clientX - r.left < r.width / 3) prev(); else next(); };
  return (
    <div className="fixed inset-0 z-[60] bg-black flex items-center justify-center" data-testid="story-viewer">
      <div className="relative w-full h-full sm:h-[92dvh] sm:max-w-[420px] sm:rounded-3xl overflow-hidden bg-slate-900 select-none">
        {story.kind === "video"
          ? <video key={story.id} ref={vref} src={imgSrc(story.media)} className="w-full h-full object-contain" autoPlay playsInline muted={muted} data-testid="story-video"
              onTimeUpdate={(e) => setProg((e.target.currentTime / (e.target.duration || 1)) * 100)} onEnded={next} />
          : <img key={story.id} src={imgSrc(story.media)} alt="" className="w-full h-full object-contain" data-testid="story-image" />}
        {story.text && <p className="absolute inset-x-6 top-1/2 -translate-y-1/2 text-center font-display text-3xl font-extrabold break-words pointer-events-none [text-shadow:0_2px_10px_rgba(0,0,0,0.5)]" style={{ color: story.color }} data-testid="story-text">{story.text}</p>}
        <div className="absolute inset-0 z-10" onClick={tap} onPointerDown={() => { setPaused(true); vref.current?.pause(); }} onPointerUp={() => { setPaused(false); vref.current?.play().catch(() => {}); }} data-testid="story-tap-area" />
        <div className="absolute top-0 inset-x-0 z-20 p-3 pt-[max(0.75rem,env(safe-area-inset-top))] space-y-3 bg-gradient-to-b from-black/60 to-transparent">
          <div className="flex gap-1">{g.stories.map((s, i) => <div key={s.id} className="flex-1 h-1 rounded-full bg-white/35 overflow-hidden"><div className="h-full bg-white" style={{ width: `${i < si ? 100 : i === si ? prog : 0}%` }} /></div>)}</div>
          <div className="flex items-center gap-2 text-white">
            <UserAvatar noStory userId={g.user.user_id} name={g.user.name} picture={g.user.picture} size="w-9 h-9" text="text-xs" />
            <p className="text-sm font-bold truncate">{g.user.name}</p><span className="text-xs text-white/70 shrink-0">{timeAgo(story.created_at)}</span>
            <div className="ml-auto flex items-center gap-1">
              {story.kind === "video" && <button onClick={() => setMuted((m) => !m)} data-testid="story-mute" className="p-2" aria-label="Ton">{muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</button>}
              {g.is_me && <button onClick={del} data-testid="story-delete" className="p-2" aria-label="Löschen"><Trash2 className="w-5 h-5" /></button>}
              <button onClick={onClose} data-testid="story-close" className="p-2" aria-label="Schließen"><X className="w-6 h-6" /></button>
            </div>
          </div>
        </div>
        {g.is_me && <button onClick={() => setViewers(true)} data-testid="story-views-btn" className="absolute bottom-4 left-4 z-20 inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-black/50 text-white text-sm font-semibold"><Eye className="w-4 h-4" /> {story.views ?? 0}</button>}
        {!g.is_me && <StoryReplyBar story={story} onFocus={() => setTyping(true)} onBlur={() => setTyping(false)} onReacted={onReacted} />}
        {viewers && <Viewers id={story.id} onClose={() => setViewers(false)} />}
      </div>
    </div>
  );
}
